import React, { useEffect, useState, useRef, useMemo, forwardRef } from 'react';
import {
    useTable,
    useRowSelect,
    useSortBy,
    useRowState,
    usePagination,
    useFlexLayout,
    useResizeColumns,
} from 'react-table';
import uniqueId from 'lodash.uniqueid';
import styled from 'styled-components';
import {
    StyledTableRow,
    StyledTable,
    StyledTableHead,
    StyledTableHeadRow,
    StyledTableBody,
    StyledColumnHeader,
    TableWrapper,
    VerticalLine,
    StyledCheckbox,
} from '../../../styles/common';
import SortIcon from '../../shared/SortIcon';
import TablePagination from '../../shared/TablePagination';
import TableSettings from '../../shared/TableSettings';
import { renderCell } from '../../../utils/generic';
import { readWidths, writeWidths, clearWidths, hasCustomWidths } from '../../../utils/columnWidthStorage';
import { useAdaptiveColumnLayout } from '../../../utils/useAdaptiveColumnLayout';
import ResizeHandle from '../TableCore/ResizeHandle';
import ResetColumnsControl from '../TableCore/ResetColumnsControl';
import TruncatedTableCell from '../TableCore/TruncatedTableCell';
import {
    DEFAULT_COLUMN_SETTINGS,
    MAX_NATURAL_COLUMN_WIDTH,
    COLLAPSE_BREAKPOINT,
    NON_TRUNCATING_COLUMNS,
} from '../TableCore/constants';
import {
    StackedTableRow,
    StackedTableCell,
    StackedCellLabel,
    StackedCellValue,
} from '../TableCore/styles';
import { useColumnResizeActions } from '../TableCore/useColumnResizeActions';

const TableSettingsWrapper = styled.div`
    text-align: right;
`;

const IndeterminateCheckbox = forwardRef(({ indeterminate, ...rest }, ref) => {
    const defaultRef = useRef();
    const resolvedRef = ref || defaultRef;

    useEffect(() => {
        resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return <StyledCheckbox type="checkbox" ref={resolvedRef} {...rest} />;
});

const ReportsTable = ({
    tableId = '',
    data = [{}],
    columnsName = [''],
    columnsData = [{}],
    selectedColumnsKey = '',
    hidePagination = false,
    hideCheckboxes = false,
    onColumnsChange = () => {},
}) => {
    const [isCheckAllClicked, setIsCheckAllClicked] = useState(false);

    // Column width persistence — same pattern as TableCore
    const [hasCustom, setHasCustom] = useState(() => tableId && hasCustomWidths(tableId));
    const savedWidths = useMemo(
        () => (tableId ? readWidths(tableId) || {} : {}),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const containerRef = useRef(null);
    const { columnWidths: adaptiveWidths, isCollapsed } = useAdaptiveColumnLayout({
        containerRef,
        columns: columnsData,
        data,
        maxNaturalWidth: MAX_NATURAL_COLUMN_WIDTH,
        collapseBelow: COLLAPSE_BREAKPOINT,
    });

    const enrichedColumns = useMemo(() => columnsData.map((col) => {
        const id = col.accessor || col.id;
        const adaptive = adaptiveWidths[id];
        return { ...col, ...(adaptive ? { width: adaptive } : {}) };
    }), [columnsData, adaptiveWidths]);

    const {
        getTableProps,
        getTableBodyProps,
        prepareRow,
        headers,
        page,
        canPreviousPage,
        canNextPage,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize, columnResizing },
        rows,
        selectedFlatRows,
        resetResizing,
        dispatch: tableDispatch,
    } = useTable(
        {
            columns: enrichedColumns,
            data,
            initialState: {
                pageIndex: 0,
                hiddenColumns: columnsData
                    .filter(column => !column.isVisible)
                    .map(column => column.accessor),
                columnResizing: { columnWidths: savedWidths },
            },
            autoResetResize: false,
            defaultColumn: DEFAULT_COLUMN_SETTINGS,
        },
        useSortBy,
        useFlexLayout,
        useResizeColumns,
        usePagination,
        useRowSelect,
        useRowState,
        ({ flatColumns }) => {
            !hideCheckboxes &&
                flatColumns &&
                flatColumns.push((columns) => [
                    {
                        id: 'selection',
                        Header: ({ getToggleAllRowsSelectedProps }) => (
                            <IndeterminateCheckbox
                                onClick={() => setIsCheckAllClicked(true)}
                                {...getToggleAllRowsSelectedProps()}
                            />
                        ),
                        Cell: ({ row: { getToggleRowSelectedProps } }) => (
                            <IndeterminateCheckbox
                                onClick={() => setIsCheckAllClicked(false)}
                                {...getToggleRowSelectedProps()}
                            />
                        ),
                    },
                    ...columns,
                ]);
        },
    );

    // Double-click auto-fit and keyboard resize, shared with TableCore so the
    // Reports table behaves identically (PM-18153: consistent across all main tables).
    // 'reports' matches the cell id prefix this table renders
    // (`reportsCell_${columnId}-${rowId}`) so auto-fit can measure rendered text.
    const { handleAutoFit, handleKeyboardResize } = useColumnResizeActions({ rows, tableDispatch, tableType: 'reports', containerRef });

    // Save widths 300ms after drag ends — same debounce pattern as TableCore
    const saveTimerRef = useRef(null);
    const { columnWidths } = columnResizing;
    useEffect(() => {
        clearTimeout(saveTimerRef.current);
        if (tableId && columnWidths && Object.keys(columnWidths).length > 0) {
            saveTimerRef.current = setTimeout(() => {
                writeWidths(tableId, columnWidths);
                setHasCustom(true);
            }, 300);
        }
        return () => clearTimeout(saveTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnWidths, tableId]);

    const handleResetColumns = () => {
        if (tableId) clearWidths(tableId);
        resetResizing();
        setHasCustom(false);
    };

    useEffect(() => {
        if (isCheckAllClicked) {
            const pageRows = selectedFlatRows.filter((a) =>
                page.some((b) => a.id === b.id),
            );
            // eslint-disable-next-line array-callback-return
            rows.map((row) => {
                prepareRow(row);
                if (!pageRows.includes(row)) {
                    row.toggleRowSelected(false);
                }
            });
            setIsCheckAllClicked(false);
        }
    }, [page, selectedFlatRows, isCheckAllClicked, rows, prepareRow]);

    const onPageSizeChange = (value) => value && setPageSize(value);

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <ResetColumnsControl visible={hasCustom} onReset={handleResetColumns} />
                <TableSettings
                    onPageSizeChange={onPageSizeChange}
                    onColumnsChange={onColumnsChange}
                    selectedColumnsKey={selectedColumnsKey}
                    columnsName={columnsName}
                    pageSize={pageSize}
                    hidePagination={hidePagination}
                />
            </div>
            <TableWrapper ref={containerRef}>
                <StyledTable
                    hover
                    borderless
                    className="mTable"
                    {...getTableProps()}
                >
                    {!isCollapsed && (
                        <StyledTableHead>
                            <StyledTableHeadRow>
                                {headers.map((column) => {
                                    if (!column.isVisible) return null;
                                    const headerProps = column.getHeaderProps(
                                        column.getSortByToggleProps(),
                                    );
                                    return (
                                        <div
                                            key={uniqueId('reportsTableHeader_')}
                                            id={`reportsTableHeader_${column.id}`}
                                            name={column.id}
                                            {...headerProps}
                                            style={{
                                                ...headerProps.style,
                                                flex: `0 0 ${column.totalWidth}px`,
                                                width: `${column.totalWidth}px`,
                                                position: 'relative',
                                            }}
                                        >
                                            <StyledColumnHeader>
                                                {column.render('Header')}
                                            </StyledColumnHeader>
                                            <SortIcon
                                                isSorted={column.isSorted}
                                                isSortedDesc={column.isSortedDesc}
                                                hide={column.id === 'actions' || column.id === 'selection'}
                                            />
                                            {column.id !== 'selection' && (
                                                <ResizeHandle
                                                    column={column}
                                                    onDoubleClick={handleAutoFit}
                                                    onKeyboardResize={handleKeyboardResize}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </StyledTableHeadRow>
                        </StyledTableHead>
                    )}
                    <StyledTableBody {...getTableBodyProps()}>
                        {page.map((row) => {
                            prepareRow(row);
                            const rowProps = row.getRowProps();
                            // A stacked row lays out vertically, so the flex-row
                            // geometry from react-table must not be applied to it.
                            const RowElement = isCollapsed ? StackedTableRow : StyledTableRow;
                            return (
                                <RowElement
                                    key={uniqueId('reportsRow_')}
                                    id={`reportsRow_${row.id}`}
                                    {...rowProps}
                                    style={isCollapsed ? undefined : rowProps.style}
                                >
                                    {row.cells.map((cell) => {
                                        const cellProps = cell.getCellProps();
                                        const cellId = `reportsCell_${cell.column.id}-${row.id}`;
                                        const cellKey = uniqueId('reportsCell_');
                                        // Interactive cells render plain and unclipped: truncating
                                        // them hides controls rather than shortening text (PM-21741).
                                        const isInteractive = NON_TRUNCATING_COLUMNS.has(cell.column.id);
                                        const cellBody = isInteractive
                                            ? renderCell(cell)
                                            : (
                                                <TruncatedTableCell>
                                                    {renderCell(cell)}
                                                </TruncatedTableCell>
                                            );

                                        if (isCollapsed) {
                                            // Only textual headers make sense as a label; the
                                            // selection and action headers render controls.
                                            const label = typeof cell.column.Header === 'string'
                                                ? cell.column.Header
                                                : null;
                                            return (
                                                <StackedTableCell key={cellKey} id={cellId}>
                                                    {label && (
                                                        <StackedCellLabel>{label}</StackedCellLabel>
                                                    )}
                                                    <StackedCellValue>
                                                        {cellBody}
                                                    </StackedCellValue>
                                                </StackedTableCell>
                                            );
                                        }

                                        return (
                                            <div
                                                key={cellKey}
                                                id={cellId}
                                                {...cellProps}
                                                style={{
                                                    ...cellProps.style,
                                                    flex: `0 0 ${cell.column.totalWidth}px`,
                                                    width: `${cell.column.totalWidth}px`,
                                                    overflow: isInteractive ? 'visible' : 'hidden',
                                                }}
                                            >
                                                {cellBody}
                                            </div>
                                        );
                                    })}
                                </RowElement>
                            );
                        })}
                    </StyledTableBody>
                </StyledTable>
            </TableWrapper>
            <TableSettingsWrapper>
                <>
                    <TablePagination
                        gotoPage={gotoPage}
                        previousPage={previousPage}
                        nextPage={nextPage}
                        canPreviousPage={canPreviousPage}
                        canNextPage={canNextPage}
                        pageIndex={pageIndex}
                        pageCount={pageCount}
                        totalCount={data.length}
                    />
                    <VerticalLine />
                </>
            </TableSettingsWrapper>
        </>
    );
};

export default ReportsTable;
